import { rfiLogImpact, rfiLogStatus } from '@/types/enums';
import dayjs from 'dayjs';
import z from 'zod';

export const post = {
  body: z.object({
    project: z.coerce.number().min(1, { error: 'Project is required' }),
    rfi_number: z.string().nonempty({ error: 'RFI number is required' }),
    subject: z.string().nonempty({ error: 'Subject is required' }),
    zone_level: z.string().nullable(),
    discipline: z.string().nullable(),
    spec_section: z.string().nullable(),
    refs: z.string().nullable(),
    status: z.enum(rfiLogStatus.map((item) => item.value)).default('open'),
    impact: z.enum(rfiLogImpact.map((item) => item.value)).default('none'),
    open_date: z
      .string()
      .nonempty({ error: 'Open date is required' })
      .refine((val) => dayjs(val, 'YYYY-MM-DD', true).isValid(), {
        message: 'Select a valid date.',
      }),
    due_date: z
      .string()
      .nonempty({ error: 'Due date is required' })
      .refine((val) => dayjs(val, 'YYYY-MM-DD', true).isValid(), {
        message: 'Select a valid date.',
      }),
    answered_date: z
      .string()
      .optional()
      .refine((val) => val === '' || dayjs(val, 'YYYY-MM-DD', true).isValid(), {
        message: 'Select a valid date.',
      }),
    closed_date: z
      .string()
      .optional()
      .refine((val) => val === '' || dayjs(val, 'YYYY-MM-DD', true).isValid(), {
        message: 'Select a valid date.',
      }),
    ball_in_court: z.string().trim().optional().or(z.literal('')),
    question: z.string().nullable(),
    official_response: z.string().nullable(),
    remarks: z.string().nullable(),

    export_to_name: z.string().optional().nullable(),
    export_spec_ref: z.string().optional().nullable(),
    export_contract_drawing_ref: z.string().optional().nullable(),
    export_shop_drawing_ref: z.string().optional().nullable(),
    export_distribution_list: z.string().optional().nullable(),
    export_information_not_on_contract: z.boolean().optional(),
    export_interpretation_of_contract: z.boolean().optional(),
    export_conflict_in_contract: z.boolean().optional(),
    export_coordination_problem: z.boolean().optional(),
  }),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string(),
    data: z.object({
      id: z.number(),
      project: z.number(),
      source: z.string(),
      external_id: z.string(),
      external_url: z.url(),
      last_synced_at: z.date(),
      rfi_number: z.string(),
      subject: z.string(),
      zone_level: z.string(),
      discipline: z.string(),
      spec_section: z.string(),
      refs: z.string(),
      status: z.string(),
      impact: z.string(),
      open_date: z.date(),
      due_date: z.date(),
      answered_date: z.date(),
      closed_date: z.date(),
      ball_in_court: z.string().trim().optional().or(z.literal('')),
      ball_in_court_name: z.string(),
      ball_in_court_display: z.string(),
      created_by: z.number(),
      created_by_name: z.string(),
      created_by_display: z.string(),
      question: z.string(),
      official_response: z.string(),
      remarks: z.string(),

      export_to_name: z.string().nullable().optional(),
      export_spec_ref: z.string().nullable().optional(),
      export_contract_drawing_ref: z.string().nullable().optional(),
      export_shop_drawing_ref: z.string().nullable().optional(),
      export_distribution_list: z.string().optional().nullable(),
      export_information_not_on_contract: z.boolean().optional(),
      export_interpretation_of_contract: z.boolean().optional(),
      export_conflict_in_contract: z.boolean().optional(),
      export_coordination_problem: z.boolean().optional(),

      attachments: z.array(
        z.object({
          id: z.number(),
        }),
      ),
      created_at: z.date(),
      updated_at: z.date(),
    }),
    errors: z.string(),
  }),
  params: z.never(),
  query: z.never(),
};
const postSchema = z.object(post);
export type Post = z.infer<typeof postSchema>;

export const patch = {
  body: post.body.partial(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string(),
    data: z.string(),
    errors: z.string(),
  }),
  params: z.object({
    rfi_log_id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchSchema = z.object(patch);
export type Patch = z.infer<typeof patchSchema>;

const get = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string(),
    data: z.array(
      z.object({
        id: z.number(),
        rfi_number: z.string(),
        subject: z.string(),
        project_id: z.number(),
        zone_level: z.string(),
        ball_in_court_display: z.string(),
        status: z.enum(rfiLogStatus.map((item) => item.value)),
        impact: z.enum(rfiLogImpact.map((item) => item.value)),
        due_date: z.date(),
        days_open: z.number(),
        spec_section: z.string(),
        refs: z.string(),
        is_overdue: z.boolean(),
      }),
    ),
    errors: z.string(),
  }),
  params: z.never(),
  query: z.object({
    bic_me: z.boolean().optional(),
    due_this_week: z.boolean().optional(),
    overdue: z.boolean().optional(),
    project: z.coerce.number({ error: 'Project is required' }),
    status: z.string().optional(),
    q: z.string().optional(),
  }),
};
const getSchema = z.object(get);
export type Get = z.infer<typeof getSchema>;

const getRfiLog = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string(),
    data: z.object({
      id: z.number(),
      project: z.number(),
      rfi_number: z.string(),
      subject: z.string(),
      zone_level: z.string(),
      discipline: z.string(),
      spec_section: z.string(),
      refs: z.string(),
      status: z.enum(rfiLogStatus.map((item) => item.value)),
      impact: z.enum(rfiLogImpact.map((item) => item.value)),
      open_date: z.date(),
      due_date: z.date(),
      answered_date: z.date().optional(),
      closed_date: z.date().optional(),
      ball_in_court: z.string().trim().optional().or(z.literal('')),
      ball_in_court_name: z.string(),
      ball_in_court_display: z.string(),
      created_by: z.number(),
      created_by_name: z.string(),
      created_by_display: z.string(),
      question: z.string(),
      official_response: z.string(),
      remarks: z.string(),

      export_to_name: z.string().nullable().optional(),
      export_spec_ref: z.string().nullable().optional(),
      export_contract_drawing_ref: z.string().nullable().optional(),
      export_shop_drawing_ref: z.string().nullable().optional(),
      export_distribution_list: z.string().optional().nullable(),
      export_information_not_on_contract: z.boolean().optional(),
      export_interpretation_of_contract: z.boolean().optional(),
      export_conflict_in_contract: z.boolean().optional(),
      export_coordination_problem: z.boolean().optional(),

      attachments: z.array(
        z.object({
          id: z.number(),
          rfi: z.number(),
          filename: z.string(),
          file: z.string(),
          external_file_url: z.string().nullable(),
          uploaded_by: z.number(),
          created_at: z.date(),
          presigned_url: z.url(),
        }),
      ),
      created_at: z.date(),
      updated_at: z.date(),
    }),
    errors: z.string(),
  }),
  params: z.object({
    rfi_log_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getRfiLogSchema = z.object(getRfiLog);
export type GetRfiLog = z.infer<typeof getRfiLogSchema>;

export const postExportLog = {
  body: z.object({
    rows: z
      .array(
        z.object({
          rfi_log_id: z.coerce.number().int().positive(),
        }),
      )
      .min(1),
  }),
  response: z.any(),
  params: z.never(),
  query: z.never(),
};
const postExportLogSchema = z.object(postExportLog);
export type PostExportLog = z.infer<typeof postExportLogSchema>;

export const postRfiLofAttachments = {
  body: z.array(
    z.object({
      filename: z.string().nonempty({ error: 'Filename is required' }),
      file: z.url().nonempty({ error: 'File is required' }),
    }),
  ),
  response: z.array(
    z.object({
      filename: z.string(),
      file: z.url(),
    }),
  ),
  params: z.object({
    rfi_log_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postRfiLofAttachmentsSchema = z.object(postRfiLofAttachments);
export type PostRfiLogAttachments = z.infer<typeof postRfiLofAttachmentsSchema>;

export const getRfiLogAttachments = {
  body: z.never(),
  response: z.object({
    presigned_url: z.string(),
  }),
  params: z.object({
    rfi_log_id: z.coerce.number(),
    attachment_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getRfiLogAttachmentsSchema = z.object(getRfiLogAttachments);
export type GetRfiLogAttachments = z.infer<typeof getRfiLogAttachmentsSchema>;

const deleteAttachment = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string(),
    data: z.string(),
    errors: z.string(),
  }),
  params: z.object({
    attachment_id: z.coerce.number(),
    rfi_log_id: z.coerce.number(),
  }),
  query: z.never(),
};
const deleteAttachmentSchema = z.object(deleteAttachment);
export type DeleteAttachment = z.infer<typeof deleteAttachmentSchema>;

export const getRfiReturnAttachments = {
  body: z.never(),
  response: z.array(
    z.object({
      id: z.number(),
      rfi: z.number().nullable().optional(),
      submittal: z.number().nullable().optional(),
      filename: z.string().nullable().optional(),
      file_url: z.string().nullable().optional(),
      uploaded_by: z
        .object({
          id: z.number().optional(),
          display_name: z.string().optional().nullable(),
          name: z.string().optional().nullable(),
        })
        .nullable()
        .optional(),
      created_at: z.date(),
    }),
  ),
  params: z.object({
    rfi_log_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getRfiReturnAttachmentsSchema = z.object(getRfiReturnAttachments);
export type GetRfiReturnAttachments = z.infer<typeof getRfiReturnAttachmentsSchema>;

export const postRfiReturnAttachment = {
  body: z.object({
    file: z.string().nonempty({ error: 'File is required' }),
    filename: z.string().optional().nullable(),
  }),
  response: z.object({
    id: z.number(),
    rfi: z.number().nullable().optional(),
    submittal: z.number().nullable().optional(),
    filename: z.string().nullable().optional(),
    file_url: z.string().nullable().optional(),
    uploaded_by: z.any().nullable().optional(),
    created_at: z.date(),
  }),
  params: z.object({
    rfi_log_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postRfiReturnAttachmentSchema = z.object(postRfiReturnAttachment);
export type PostRfiReturnAttachment = z.infer<typeof postRfiReturnAttachmentSchema>;

export const deleteRfiReturnAttachment = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    return_id: z.coerce.number(),
    rfi_log_id: z.coerce.number(),
  }),
  query: z.never(),
};
const deleteRfiReturnAttachmentSchema = z.object(deleteRfiReturnAttachment);
export type DeleteRfiReturnAttachment = z.infer<
  typeof deleteRfiReturnAttachmentSchema
>;
